import {
  Button,
  FormHelperText,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { ReactElement } from "react";

import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  MediaFieldValue,
  MediaPreview,
} from "~frontend/components/forms/FormikMediaField";
import { CampaignPersonalizedGifPreview } from "~frontend/components/personalizedGifs/CampaignPersonalizedGifPreview";
import { palette } from "~frontend/theme";

const PreviewValid = styled("div")({
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
});

const PreviewInvalid = styled("div")({
  fontStyle: "italic",
});

const SampleContactTextFieldContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  columnGap: theme.spacing(2),
  rowGap: theme.spacing(1),
}));

const SampleContactTextField = styled(TextField)({});

function SampleContactEditor({
  sampleContact,
  onChange,
}: {
  sampleContact: Record<string, string>;
  onChange: (field: string, val: string) => void;
}) {
  return (
    <SampleContactTextFieldContainer>
      {Object.entries(sampleContact).map(([field, val]) => (
        <SampleContactTextField
          key={field}
          label={field}
          value={val}
          onChange={e => onChange(field, e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      ))}
    </SampleContactTextFieldContainer>
  );
}

export function ScriptPreview({
  campaignId,
  media,
  personalizedGifId,
  campaignPersonalizedGifId,
  renderedTemplate,
  errorMessage,
  sampleContact = {},
  onSampleContactChange,
  onSendPreviewMessage,
  sendPreviewDisabled,
}: {
  campaignId?: string | null;
  media: MediaFieldValue;
  personalizedGifId?: string | null;
  campaignPersonalizedGifId?: string | null;
  renderedTemplate: React.ReactNode;
  errorMessage: string | null;
  sampleContact?: Record<string, string>;
  onSampleContactChange?: (newSampleContact: Record<string, string>) => void;
  onSendPreviewMessage?: (opts: {
    contactData: Record<string, string>;
  }) => void;
  sendPreviewDisabled?: string | null;
}): ReactElement {
  return (
    <FlexBox direction="column">
      <CollapsibleDrawer title="Message Preview" startOpen={false}>
        <FormHelperText sx={{ fontSize: "12px", py: 1 }}>
          Edit the contact fields below to preview your message with different
          variables.
        </FormHelperText>
        <SampleContactEditor
          sampleContact={sampleContact}
          onChange={(field, val) => onSampleContactChange?.({ [field]: val })}
        />

        <FlexBox
          direction="column"
          alignItems="flex-start"
          gap={2}
          sx={{
            mt: 2,
            color: palette.darkGray,
            borderRadius: "20px 20px 0 20px",
            backgroundColor: palette.lightBlue,
            px: 4,
            py: 3,
          }}
        >
          {errorMessage ? (
            <PreviewInvalid>{errorMessage}</PreviewInvalid>
          ) : (
            <>
              <MediaPreview media={media} />
              {campaignId &&
                (campaignPersonalizedGifId || personalizedGifId) && (
                  <CampaignPersonalizedGifPreview
                    campaignPersonalizedGifId={campaignPersonalizedGifId}
                    personalizedGifId={personalizedGifId}
                    campaignId={campaignId}
                    sampleContactData={sampleContact}
                    sx={{ height: "100px", width: "100px" }}
                  />
                )}
              <PreviewValid>{renderedTemplate}</PreviewValid>
            </>
          )}
        </FlexBox>

        {onSendPreviewMessage && (
          <FlexBox justifyContent="end" sx={{ mt: 2 }}>
            {sendPreviewDisabled && (
              <FlexBox alignItems="center" sx={{ mr: 1 }}>
                <Typography variant="body3" color={palette.darkGray}>
                  {sendPreviewDisabled}
                </Typography>
              </FlexBox>
            )}
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={() =>
                onSendPreviewMessage({ contactData: sampleContact })
              }
              disabled={!!sendPreviewDisabled}
              sx={{ padding: "6px 8px", height: "auto" }}
            >
              <Typography variant="h4Caps">Send A Preview Text</Typography>
            </Button>
          </FlexBox>
        )}
      </CollapsibleDrawer>
    </FlexBox>
  );
}
